import { ArrowLeft, ArrowRight } from 'lucide-react'
import { AnimatedStatusButton } from '#app/components/ui/animated-status-button'
import { Card, CardContent } from '#app/components/ui/card'

export function ScreeningBackLink({
	onBack,
	disabled = false,
}: {
	onBack: () => void
	disabled?: boolean
}) {
	return (
		<button
			type="button"
			onClick={onBack}
			disabled={disabled}
			className="mb-3 flex items-center gap-1 text-sm text-[#4F4F4F] hover:text-[#32302F] disabled:opacity-50"
		>
			<ArrowLeft className="h-4 w-4" />
			Back
		</button>
	)
}

interface ScreeningStepContainerProps {
	title: string
	des?: string
	children: React.ReactNode
	onNext?: () => void
	onBack?: () => void
	isSubmitting?: boolean
	canGoNext?: boolean
	showBackButton?: boolean
	isEditMode?: boolean
	nextButtonText?: string
	isStepCompleted?: boolean
}

/**
 * Reusable container component for screening steps
 * Provides consistent gradient background, white cards, and navigation buttons
 */
export function ScreeningStepContainer({
	title,
	des,
	children,
	onNext,
	onBack,
	isSubmitting = false,
	canGoNext = true,
	showBackButton = true,
	isEditMode = false,
	nextButtonText,
	isStepCompleted = false,
}: ScreeningStepContainerProps) {
	return (
		<div className="screening-step mt-20 px-4 pt-2 pb-8 select-none sm:px-6 lg:px-25 [&_input]:select-text [&_textarea]:select-text">
			<div className="mx-auto w-full">
				{/* Title - Top Left */}
				<h1 className="font-calgary mb-3 text-[28px] text-[#32302F] sm:text-[22px] md:text-[26px] lg:pt-3 lg:text-[30px]">
					{title}
				</h1>
				{des && (
					<p className="text-regular mb-6 text-[18px] text-[#32302F]">{des}</p>
				)}

				{/* Form Content */}
				<div className="mb-9 border-b border-[#F0F0F0] pb-8">{children}</div>

				{/* Navigation Buttons */}
				<div className="flex flex-row justify-between border-[#CCCCCC] sm:flex-row">
					{/* Back whenever the parent provides onBack (completed steps included). */}
					{showBackButton && onBack && (
						<AnimatedStatusButton
							type="button"
							onClick={onBack}
							disabled={isSubmitting}
							status="idle"
							className="w-full shrink-0 rounded-[30px] border border-[#CCCCCC] bg-white px-6 py-[12px] text-[16px] font-medium text-[#32302F] transition-colors duration-200 hover:bg-gray-50 sm:w-auto sm:px-8 sm:py-[16px]"
						>
							<ArrowLeft />
							Back
						</AnimatedStatusButton>
					)}
					<div className="flex-1" />
					{onNext && (
						<AnimatedStatusButton
							type="button"
							onClick={onNext}
							disabled={isSubmitting || !canGoNext}
							status={isSubmitting ? 'pending' : 'idle'}
							className="w-full rounded-[30px] border-0 bg-[#32302F] px-6 py-[12px] text-[16px] font-medium text-white transition-colors duration-200 hover:bg-[#1a1918] active:bg-[#0f0f0e] sm:w-auto sm:px-8 sm:py-[16px]"
						>
							{nextButtonText ||
								(title === 'Review Application' ||
								title === 'Review Guarantor Application'
									? 'Submit Application'
									: 'Next')}
							{/* <span>→</span> */}
							<ArrowRight />
						</AnimatedStatusButton>
					)}
				</div>
			</div>
		</div>
	)
}

/**
 * Reusable white card component for step sections
 */
export function ScreeningSectionCard({
	title,
	children,
	des,
}: {
	title?: string
	children: React.ReactNode
	des?: string
}) {
	return (
		<Card className="rounded-[30px] bg-[#FBFDFD] shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
			<CardContent>
				{title && (
					<h2 className="mb-4 text-[18px] font-medium text-[#32302F] sm:mb-6 md:text-[22px]">
						{title}
					</h2>
				)}
				{des && <p>{des}</p>}
				<div className="space-y-4 sm:space-y-6">{children}</div>
			</CardContent>
		</Card>
	)
}
